// TanStack Query keys, in one place so invalidations name the same thing.
// A filter is sorted before it enters a key: the same sources in another
// order are the same list.
const sorted = (values: string[] | null) => (values === null ? "all" : [...values].sort());

export const keys = {
  me: ["me"] as const,
  sources: ["sources"] as const,
  visit: ["visit"] as const,
  /** The PUTs of the favourites: one queue, so they reach the api in the order of the clicks. */
  followSources: ["follow-sources"] as const,
  /** The PUTs and DELETEs of the bookmarks: one queue, in the order of the clicks. */
  saveItem: ["save-item"] as const,
  /** DELETE /me/saved, «Pulisci tutto»: every bookmark at once. */
  clearSaved: ["clear-saved"] as const,
  /** Every feed list, whatever its bucket or filter: for a cache-wide update. */
  feedAll: ["feed"] as const,
  /**
   * One list of Ultime news; `sources` null means every source. `scope` is
   * the id of a feed (Il mio feed), `"favourites"` (the pinned sources) or
   * null (the plain list). It stays at index 3, where `isFeedList` reads it.
   */
  feed: (bucket: "new" | "previous", sources: string[] | null, scope: string | null = null) =>
    ["feed", bucket, sorted(sources), scope ?? "latest"] as const,
  /** Whether a key is one of the lists of the given feed (Il mio feed): for `predicate` in an invalidation. */
  isFeedList: (key: readonly unknown[], feedId: string) => key[0] === "feed" && key[3] === feedId,
  /** The caller's feeds (Il mio feed): names and sources. */
  feeds: ["feeds"] as const,
  /** Every item detail, whatever its origin or id: for a cache-wide invalidation. */
  itemAll: ["item"] as const,
  /** One item detail, the text of a card opened in place. */
  item: (origin: string, id: string) => ["item", origin, id] as const,
  /** Every list of News salvate, whatever its months and weeks. */
  savedAll: ["saved"] as const,
  /** One list of News salvate: whole months and single weeks, null when none is named. */
  saved: (months: string[] | null, weeks: string[] | null = null) => ["saved", sorted(months), sorted(weeks)] as const,
  /** The index of a Parliament document: every entry of GET /items/{origin}/{item_id}/sections. */
  sections: (origin: string, id: string) => ["sections", origin, id] as const,
  /** Every page of the sittings, whatever the filter: for a cache-wide invalidation. */
  sittingsAll: ["sittings"] as const,
  /** One list of sittings (Ultime news with sources of sittings), by chamber and session type (null: both / every type). */
  sittings: (chamber: string | null, sessionType: string | null) =>
    ["sittings", chamber ?? "both", sessionType ?? "all"] as const,
  /** One sitting with its documents, by its natural key. */
  sitting: (chamber: string, sessionType: string, number: number) => ["sitting", chamber, sessionType, number] as const,
  /** Every sitting detail, whatever its key: its documents carry the caller's bookmarks. */
  sittingAll: ["sitting"] as const,
  /**
   * The reduced index of a sitting (GET /parliament/sittings/{chamber}/{session_type}/{number}/sections),
   * keyed with the sitting's `updated_at`: a sitting that changed is another
   * index, the one in the cache is never served for it.
   */
  sittingIndex: (chamber: string, sessionType: string, number: number, updatedAt: string | null) =>
    ["sitting-index", chamber, sessionType, number, updatedAt ?? "none"] as const,
};
